import _ from 'lodash'
import PropTypes from 'prop-types'
import { useMemo } from 'react'

import { ActionButtons } from '@/components/ActionButtons'
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/elements/drawer'
import { cn } from '@/utils/helpers'

/**
 *
 * @param {object} props
 * @param {import('../ActionButtons/ActionButtons').ActionButton[]} [props.actions] Array of action objects
 * @param {string} [props.direction] Direction from which the panel should slide in ('left'|'right'|'top'|'bottom')
 * @param {import('react').JSX.Element} props.content The content to display inside the panel
 * @param {string} [props.contentClassName] Additional Tailwind classes applied to the drawer content (e.g. to override width)
 * @param {boolean} [props.loading] Whether to show a loading state on the action buttons
 * @param {boolean} [props.open] Whether the panel is open
 * @param {Function} props.setOpen Function to set the open state
 * @param {string} props.title The title to display at the top of the panel
 * @returns
 */
const SlidePanel = ({
  actions = [{ type: 'cancel', label: 'Cancel' }],
  direction = 'right',
  content,
  contentClassName,
  loading = false,
  open = true,
  setOpen,
  title,
  ...rest
}) => {
  const parsedActions = useMemo(() => {
    // There should always be a cancel action to close the panel
    const cancel = _.find(actions, (a) => a.type === 'cancel')
    const fallbackCancel = {
      type: 'cancel',
      label: 'Cancel',
      onClick: () => {
        setOpen(false)
      },
    }
    return [...(!cancel ? [fallbackCancel] : []), ...actions]
  }, [actions, setOpen])

  return (
    <Drawer direction={direction} open={open} onClose={() => setOpen(false)} {...rest}>
      <DrawerContent className={cn('w-screen! sm:min-w-xl!', contentClassName)}>
        <DrawerHeader>
          <DrawerTitle>{title}</DrawerTitle>
        </DrawerHeader>

        <div className="overflow-y-scroll p-4">{content}</div>

        <DrawerFooter className="items-end">
          <ActionButtons actions={parsedActions} loading={loading} />
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}

SlidePanel.propTypes = {
  actions: PropTypes.arrayOf(PropTypes.object),
  content: PropTypes.node.isRequired,
  contentClassName: PropTypes.string,
  direction: PropTypes.string,
  loading: PropTypes.bool,
  open: PropTypes.bool,
  setOpen: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
}

export default SlidePanel
